import { Fragment, useContext, useEffect, useRef, useState } from "react";
import { toast } from "../../helper/common";
import ApplicationContext from "../../context/application/ApplicationContext";
import useTranslator from "../../hooks/useTranslator";
import AuthContext from "../../context/auth/AuthContext";
import { Tooltip as ReactTooltip } from "react-tooltip";

import {
  resolveParticipantName,
  getApplicationTitle,
} from "../../helper/applicationHelpers";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import ClientIdentificationForm from "../ClientIdentificationForm/ClientIdentificationForm";
import constants from "../../Utils/constants";
import {
  RowButton,
  Avatar,
  Badge,
  Button,
  Drawer,
  Icon,
  Menu,
  SkeletonList,
} from "../design";
import "./ApplicationsModal.css";

const resolveCreatorName = (created_by) => {
  if (!created_by || typeof created_by === "string") return "";
  const fname = created_by.fname || created_by.firstname || "";
  const lname = created_by.lname || created_by.lastname || "";
  return `${fname} ${lname}`.trim() || created_by.email || "";
};

const resolveCreatorEmail = (created_by) => {
  if (!created_by || typeof created_by === "string") return null;
  return created_by.email ?? null;
};

const resolveCreatorId = (created_by) => {
  if (!created_by) return null;
  if (typeof created_by === "string") return created_by;
  return created_by._id ?? null;
};

const getParticipantGuarantors = (participant) =>
  participant?.consent_status === "accepted" &&
  Array.isArray(participant?.guarantors)
    ? participant.guarantors
    : [];
const formatPaymentTimeRemaining = (expiresAt, now) => {
  const remainingMs = Math.max(0, new Date(expiresAt).getTime() - now);
  const totalMinutes = Math.floor(remainingMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

/* ── role badge ── */
const ROLE_BADGES = {
  deleted: { tone: "neutral", key: "deletedUser", fallback: "Deleted user" },
  owner: { tone: "ink", key: "owner", fallback: "Owner" },
  invited: { tone: "warning", key: "invited", fallback: "Invited" },
  accepted: { tone: "lime", key: "accepted", fallback: "Accepted" },
  pending: { tone: "warning", key: "pending", fallback: "Pending" },
  rejected: { tone: "danger", key: "rejected", fallback: "Rejected" },
};

const RoleBadge = ({ role, _t }) => {
  const badge = ROLE_BADGES[role];
  if (!badge) return null;
  return <Badge tone={badge.tone}>{_t(badge.key, badge.fallback)}</Badge>;
};

const ClientIdentificationPendingBadge = ({ canSign, onSign, _t }) => {
  const badge = <Badge tone="warning">{_t("amlSigningPending")}</Badge>;
  if (canSign) {
    return (
      <RowButton
        type="button"
        className="applications-badge-button"
        onClick={(event) => {
          event.stopPropagation();
          onSign();
        }}
      >
        {badge}
      </RowButton>
    );
  }

  return badge;
};

const MoreIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <circle cx="12" cy="5" r="1.6" />
    <circle cx="12" cy="12" r="1.6" />
    <circle cx="12" cy="19" r="1.6" />
  </svg>
);

/* ── single application card ── */
const ApplicationCard = ({
  app,
  currentUserEmail,
  currentUserId,
  currentUserClientIdentificationStatus,
  onAccept,
  onReject,
  onDelete,
  processingId,
  openMenuId,
  setOpenMenuId,
  onCloseModal,
  llShowOnly,
  onSignClientIdentification,
  pendingSkorProtectAction,
  now,
  onPaySkorProtect,
  paymentProcessingId,
  onViewPaymentReceipt,
}) => {
  const { _t } = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const menuRef = useRef(null);
  const isProcessing = processingId === app._id;
  const menuOpen = openMenuId === app._id;
  const skorProtectActionStatus =
    pendingSkorProtectAction?.status ?? "waiting_for_payment";
  const isSkorProtectPaymentPending =
    skorProtectActionStatus === "waiting_for_payment";
  const isSkorProtectPaymentReceived =
    skorProtectActionStatus === "waiting_for_rental_contract";
  const isSkorProtectActive = skorProtectActionStatus === "active";
  const showSkorProtectDocuments =
    isSkorProtectPaymentReceived || isSkorProtectActive;
  const activePendingSkorProtectAction =
    pendingSkorProtectAction &&
    (showSkorProtectDocuments ||
      (isSkorProtectPaymentPending &&
        new Date(pendingSkorProtectAction.expires_at).getTime() > now))
      ? pendingSkorProtectAction
      : null;
  const isPaymentProcessing =
    paymentProcessingId === activePendingSkorProtectAction?.skor_protect_id;

  const participants = app.participants ?? [];
  const creatorEmail = resolveCreatorEmail(app.created_by);
  const creatorId = resolveCreatorId(app.created_by);
  const iAmOwner =
    (creatorId && creatorId === currentUserId) ||
    (creatorEmail && creatorEmail.toLowerCase() === currentUserEmail);

  const isPendingInvitation =
    app.isPendingInvitation ??
    (!iAmOwner &&
      participants.some(
        (p) =>
          p.email?.toLowerCase() === currentUserEmail &&
          p.consent_status === "pending"
      ));

  const pendingCount = participants.filter(
    (p) =>
      p.consent_status === "pending" &&
      p.email?.toLowerCase() !== creatorEmail?.toLowerCase()
  ).length;

  const shareRequests = app.shareRequests ?? [];
  const sharedProfiles = (app.sharedProfiles ?? []).filter(
    (sharedProfile) =>
      sharedProfile?.id?._id || sharedProfile?.shared_by_deleted
  );
  const totalShared = shareRequests.length + sharedProfiles.length;
  const clientIdentificationRequired =
    app.client_identification_required === true;
  const currentParticipant = participants.find(
    (participant) =>
      participant?.id?.toString() === currentUserId?.toString() ||
      participant?.email?.toLowerCase() === currentUserEmail?.toLowerCase()
  );
  const currentUserClientIdentificationPending =
    clientIdentificationRequired &&
    currentParticipant?.consent_status === constants.ACCEPTED &&
    (currentParticipant?.client_identification_consent_status ??
      currentUserClientIdentificationStatus) !== constants.ACCEPTED;
  const showSkorProtectStatus = !currentUserClientIdentificationPending;

  /* status badge under the card header */
  const getStatusBadge = () => {
    if (isPendingInvitation) {
      const name = resolveCreatorName(app.created_by);
      return (
        <Badge tone="warning">
          {_t("invitationFrom")} {name}
        </Badge>
      );
    }

    if (pendingCount > 0) {
      return (
        <Badge tone="warning">
          {_t("pendingConsent")} &bull; {pendingCount}{" "}
          {pendingCount === 1 ? _t("flatmate") : _t("flatmates")}
        </Badge>
      );
    }

    if (totalShared > 0) {
      return (
        <Badge tone="lime">
          {_t("sharedWithApplication")} {totalShared}{" "}
          {totalShared === 1 ? _t("landlordApp") : _t("landlordsApp")}
        </Badge>
      );
    }

    return null;
  };

  /* determine the role of a single participant */
  const getRole = (participant) => {
    if (participant.is_deleted || participant.deleted) return "deleted";
    const isOwnerParticipant =
      creatorEmail &&
      participant.email?.toLowerCase() === creatorEmail.toLowerCase();
    if (isOwnerParticipant) return "owner";

    const isMe = participant.email?.toLowerCase() === currentUserEmail;
    if (isMe && participant.consent_status === "pending") return "invited";

    return participant.consent_status ?? "pending";
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpen]);

  const menuItems = [];
  if (showSkorProtectDocuments) {
    menuItems.push(
      {
        key: "receipt",
        label: _t("viewPaymentReceipt"),
        icon: <Icon name="fileText" />,
        onClick: () => {
          setOpenMenuId(null);
          onViewPaymentReceipt(activePendingSkorProtectAction);
        },
      },
      {
        key: "terms",
        label: _t("viewSkorProtectTermsAndConditions"),
        icon: <Icon name="shield" />,
        onClick: () => {
          setOpenMenuId(null);
          window.open(
            "https://www.skortorent.com/inquilino/skor-protect/",
            "_blank",
            "noopener,noreferrer"
          );
        },
      }
    );
  }
  menuItems.push(
    {
      key: "share",
      label: _t("shareApplicationWithLandlord", "Share with landlord"),
      icon: <Icon name="send" />,
      onClick: () => {
        setOpenMenuId(null);
        onCloseModal();
        const url = new URL(window.location.href);
        url.searchParams.set("shareprofilemodal", "true");
        url.searchParams.set("applicationId", app._id);
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`
        );
        window.dispatchEvent(new Event("open-share-profile-modal"));
      },
    },
    {
      key: "delete",
      danger: true,
      label: isProcessing
        ? _t("deleting", "Deleting...")
        : _t("deleteApplication", "Delete application"),
      icon: <Icon name="trash" />,
      onClick: () => {
        if (!isProcessing) {
          setOpenMenuId(null);
          onDelete(app._id);
        }
      },
    }
  );

  return (
    <div
      onClick={() => {
        setExpanded((v) => !v);
        setOpenMenuId(null);
      }}
      className="applications-card"
    >
      {/* ── header row ── */}
      <div className="applications-card__row">
        <div className="applications-card__info">
          <div className="applications-card__title-row">
            <p
              className="applications-card__name"
              title={getApplicationTitle(app, _t)}
            >
              {getApplicationTitle(app, _t)}
            </p>
            {participants.length > 0 && (
              <Badge tone="neutral">
                <Icon name="users" size={12} />
                {participants.length}
              </Badge>
            )}
            {app.skor_protect && <SkorProtectBadge />}
          </div>
          <div className="applications-card__chips">
            {currentUserClientIdentificationPending && (
              <Badge tone="warning">{_t("amlSigningPending")}</Badge>
            )}
            {showSkorProtectStatus &&
              activePendingSkorProtectAction &&
              isSkorProtectPaymentPending && (
                <Badge tone="warning">
                  {_t("skorProtect")} · {_t("paymentPending")} ·{" "}
                  {formatPaymentTimeRemaining(
                    activePendingSkorProtectAction.expires_at,
                    now
                  )}
                </Badge>
              )}
            {showSkorProtectStatus &&
              activePendingSkorProtectAction &&
              isSkorProtectPaymentReceived && (
                <>
                  <span data-tooltip-id={`skor-protect-paid-${app._id}`}>
                    <Badge tone="lime">
                      <Icon name="shield" size={12} />
                      {_t("skorProtect")} · {_t("paid")}
                      <Icon name="info" size={12} />
                    </Badge>
                  </span>
                  <ReactTooltip
                    id={`skor-protect-paid-${app._id}`}
                    place="bottom"
                    className="react-tooltip-styling application-skor-protect-paid-tooltip"
                    render={() => (
                      <p>
                        {_t(
                          "skorProtectPaidTooltip",
                          "Payment received. Skor Protect will become active as soon as the landlord uploads the rental agreement."
                        )}
                      </p>
                    )}
                  />
                </>
              )}
            {showSkorProtectStatus &&
              activePendingSkorProtectAction &&
              isSkorProtectActive && (
                <Badge tone="lime">
                  <Icon name="shield" size={12} />
                  {_t("skorProtect")} · {_t("active")}
                </Badge>
              )}
          </div>
          <div className="applications-avatars">
            {participants.slice(0, 4).map((p, i) => {
              const name = resolveParticipantName(
                p,
                _t("deletedUser", "Deleted user")
              );
              const isPending = p.consent_status === "pending";
              const guarantors = getParticipantGuarantors(p);
              return (
                <Fragment key={i}>
                  <span
                    className={`applications-avatars__item${isPending ? " is-pending" : ""}`}
                    title={name}
                  >
                    <Avatar name={name} size="sm" />
                  </span>
                  {guarantors.slice(0, 3).map((guarantor, guarantorIndex) => {
                    const guarantorName = resolveParticipantName(guarantor);
                    return (
                      <span
                        key={guarantor._id ?? guarantorIndex}
                        className="applications-avatars__item is-guarantor"
                        title={`${guarantorName} · ${_t("guarantor", "Guarantor")}`}
                      >
                        <Avatar name={guarantorName} size="sm" />
                      </span>
                    );
                  })}
                  {guarantors.length > 3 && (
                    <Badge tone="neutral">+{guarantors.length - 3}</Badge>
                  )}
                </Fragment>
              );
            })}
            {participants.length > 4 && (
              <Badge tone="neutral">+{participants.length - 4}</Badge>
            )}
          </div>
        </div>

        <div className="applications-card__controls">
          {isPendingInvitation ? (
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onReject(app._id);
                }}
                disabled={isProcessing}
              >
                {_t("decline", "Decline")}
              </Button>
              <Button
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept(app._id);
                }}
                disabled={isProcessing}
              >
                {_t("accept", "Accept")}
              </Button>
            </>
          ) : !llShowOnly ? (
            <div
              className="applications-card__menu-anchor"
              ref={menuRef}
              onClick={(e) => e.stopPropagation()}
            >
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label="menu"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                iconStart={<MoreIcon />}
                onClick={() => setOpenMenuId(menuOpen ? null : app._id)}
              />
              {menuOpen && (
                <Menu className="applications-card__menu" items={menuItems} />
              )}
            </div>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            aria-label="toggle"
            aria-expanded={expanded}
            iconStart={
              <Icon
                name="chevronDown"
                className={`applications-card__chevron${expanded ? " is-open" : ""}`}
              />
            }
          />
        </div>
      </div>

      {/* ── status badge + action ── */}
      {((!llShowOnly && getStatusBadge()) ||
        currentUserClientIdentificationPending ||
        (activePendingSkorProtectAction && isSkorProtectPaymentPending)) && (
        <div className="applications-card__footer">
          {!llShowOnly && getStatusBadge()}
          {!llShowOnly && currentUserClientIdentificationPending ? (
            <Button
              size="sm"
              iconEnd={<Icon name="arrowRight" />}
              onClick={(event) => {
                event.stopPropagation();
                onSignClientIdentification(app);
              }}
            >
              {_t("viviaSign")}
            </Button>
          ) : activePendingSkorProtectAction && isSkorProtectPaymentPending ? (
            <Button
              size="sm"
              loading={isPaymentProcessing}
              iconEnd={<Icon name="arrowRight" />}
              onClick={async (event) => {
                event.stopPropagation();
                await onPaySkorProtect(activePendingSkorProtectAction);
              }}
            >
              {isPaymentProcessing ? _t("redirecting") : _t("paySkorProtect")}
            </Button>
          ) : null}
        </div>
      )}

      {/* ── expanded: tenants list ── */}
      {expanded && (
        <>
          {participants.length > 0 && (
            <div className="applications-card__section">
              <p className="applications-card__label">
                {_t("tenantsInThisApplication", "TENANTS IN THIS APPLICATION")}
              </p>
              {participants.map((p, i) => {
                const role = getRole(p);
                const guarantors = getParticipantGuarantors(p);
                const isDeleted = p.is_deleted === true;
                const isAccepted = p.consent_status === "accepted";
                const isMe =
                  !isDeleted && p.email?.toLowerCase() === currentUserEmail;
                const isOwnerRow = role === "owner";
                const participantId = p.id;
                const showClientIdentificationConsentStatus =
                  clientIdentificationRequired && isAccepted && !isDeleted;
                const isClientIdentificationConsentPending =
                  showClientIdentificationConsentStatus &&
                  p.client_identification_consent_status !== constants.ACCEPTED;
                const hasTenantId = participantId && llShowOnly && !isDeleted;
                const canNavigate = hasTenantId && !isMe;

                /* display name: always show "You" when it's the current user */
                const displayName = isMe
                  ? _t("you", "You")
                  : resolveParticipantName(
                      p,
                      _t("deletedUser", "Deleted user")
                    );

                /* subtitle: "Owner" label for owner row, otherwise email */
                const subtitle = isDeleted
                  ? ""
                  : isOwnerRow
                    ? _t("owner", "Owner")
                    : (p.email ?? "");

                return (
                  <Fragment key={participantId ?? p.email ?? i}>
                    <div
                      className={`applications-member${canNavigate ? " is-link" : ""}${isDeleted ? " is-deleted" : ""}`}
                      onClick={() => {
                        if (canNavigate) {
                          const url = new URL(window.location.href);
                          url.searchParams.set("tenant_id", participantId);
                          window.history.pushState({}, "", url.toString());
                          window.dispatchEvent(new PopStateEvent("popstate"));
                        }
                      }}
                    >
                      <Avatar
                        name={isDeleted ? "" : isMe ? "Yo" : displayName}
                        size="md"
                      />
                      <div className="applications-member__info">
                        <p className="applications-member__name">
                          {displayName}
                        </p>
                        <p className="applications-member__email">{subtitle}</p>
                      </div>
                      <div className="applications-member__status">
                        {isClientIdentificationConsentPending ? (
                          <ClientIdentificationPendingBadge
                            canSign={isMe && !llShowOnly}
                            onSign={() => onSignClientIdentification(app)}
                            _t={_t}
                          />
                        ) : (
                          <RoleBadge role={role} _t={_t} />
                        )}
                      </div>
                      {canNavigate && <Icon name="chevronRight" size={16} />}
                    </div>
                    {guarantors.map((guarantor, guarantorIndex) => {
                      const guarantorName = resolveParticipantName(guarantor);
                      return (
                        <div
                          key={guarantor._id ?? guarantorIndex}
                          className="applications-member is-guarantor"
                        >
                          <Avatar name={guarantorName} size="md" />
                          <div className="applications-member__info">
                            <p className="applications-member__name">
                              {guarantorName}
                            </p>
                            {guarantor.email && (
                              <p
                                className="applications-member__email"
                                title={guarantor.email}
                              >
                                {guarantor.email}
                              </p>
                            )}
                          </div>
                          <div className="applications-member__status">
                            <Badge tone="neutral">
                              {_t("guarantor", "Guarantor")}
                            </Badge>
                          </div>
                        </div>
                      );
                    })}
                  </Fragment>
                );
              })}
            </div>
          )}

          {/* ── expanded: shared with (tenants only — hidden in landlord view) ── */}
          {!llShowOnly && totalShared > 0 && (
            <div className="applications-card__section applications-card__section--shared">
              <p className="applications-card__label">
                {_t("sharedWithSection", "SHARED WITH")}
              </p>

              {shareRequests.map((sr, i) => {
                const landlord = sr.landlord_id ?? {};
                const sharedBy = sr.tenant_id ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const landlordEmail = landlord.email ?? "";
                const sharedByName =
                  `${sharedBy.fname ?? ""} ${sharedBy.lname ?? ""}`.trim() ||
                  sharedBy.email ||
                  "";
                return (
                  <div key={sr._id ?? i} className="applications-member">
                    <Avatar name={landlordName || landlordEmail} size="md" />
                    <div className="applications-member__info">
                      <p className="applications-member__name">
                        {landlordName}
                      </p>
                      <p
                        className="applications-member__email"
                        title={landlordEmail}
                      >
                        {landlordEmail}
                      </p>
                    </div>
                    <div className="applications-member__status applications-member__status--stack">
                      {sharedByName && (
                        <span className="applications-member__email">
                          {_t("sharedBy", "Shared by")} {sharedByName}
                        </span>
                      )}
                      <Badge tone="warning">{_t("pending", "Pending")}</Badge>
                    </div>
                  </div>
                );
              })}

              {sharedProfiles.map((sp, i) => {
                const landlord = sp.id?.landlordid ?? {};
                const sharedBy = sp.shared_by ?? {};
                const landlordName =
                  `${landlord.fname_companyName ?? ""} ${landlord.lname ?? ""}`.trim() ||
                  landlord.email ||
                  "";
                const landlordEmail = landlord.email ?? "";
                const sharedByName = sp.shared_by_deleted
                  ? _t("deletedUser", "Deleted user")
                  : `${sharedBy.fname ?? ""} ${sharedBy.lname ?? ""}`.trim() ||
                    sharedBy.email ||
                    "";
                return (
                  <div key={sp.id?._id ?? i} className="applications-member">
                    <Avatar name={landlordName || landlordEmail} size="md" />
                    <div className="applications-member__info">
                      <p className="applications-member__name">
                        {landlordName}
                      </p>
                      <p
                        className="applications-member__email"
                        title={landlordEmail}
                      >
                        {landlordEmail}
                      </p>
                    </div>
                    {sharedByName && (
                      <div className="applications-member__status">
                        <span className="applications-member__email">
                          {_t("sharedBy", "Shared by")} {sharedByName}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* ── main modal ── */
const ApplicationsModal = ({
  isOpen,
  onClose,
  applications,
  loading,
  onNewApplication,
  onApplicationsChange,
  llShowOnly,
  pendingSkorProtectActions = [],
}) => {
  const { _t } = useTranslator();
  const { user, loadUser } = useContext(AuthContext);
  const currentUserEmail = user?.email?.toLowerCase();
  const currentUserId = user?._id;

  const {
    consentApplication,
    deleteApplication,
    createSkorProtectPaymentLink,
    loadPendingSkorProtectActions,
    getSkorProtectPaymentReceipt,
    reloadApplications,
  } = useContext(ApplicationContext);
  const [processingId, setProcessingId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [clientIdentificationSigning, setClientIdentificationSigning] =
    useState(null);
  const [paymentProcessingId, setPaymentProcessingId] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const resetPaymentProcessing = () => setPaymentProcessingId(null);

    window.addEventListener("pageshow", resetPaymentProcessing);
    return () => window.removeEventListener("pageshow", resetPaymentProcessing);
  }, []);

  useEffect(() => {
    if (!isOpen || pendingSkorProtectActions.length === 0) return undefined;

    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [isOpen, pendingSkorProtectActions.length]);

  const handleAccept = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await consentApplication(applicationId, "accepted");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToAcceptRequest"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await consentApplication(applicationId, "rejected");
    } catch (err) {
      toast.error(err?.response?.data?.message ?? _t("failedToRejectRequest"));
    } finally {
      setProcessingId(null);
    }
  };

  const handleDelete = async (applicationId) => {
    try {
      setProcessingId(applicationId);
      await deleteApplication(applicationId);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ?? _t("failedToDeleteApplication")
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handleSignClientIdentification = (application) => {
    setClientIdentificationSigning({ application });
  };

  if (clientIdentificationSigning) {
    return (
      <ClientIdentificationForm
        user={user}
        workspace={user?.workspaceId}
        applicationId={clientIdentificationSigning.application._id}
        onClose={() => setClientIdentificationSigning(null)}
        onSubmitted={async () => {
          setClientIdentificationSigning(null);
          try {
            await reloadApplications();
            await loadUser?.("tenant");
          } catch (err) {
            toast.error(
              err?.response?.data?.message ?? _t("failedToAcceptRequest")
            );
          }
        }}
      />
    );
  }

  const handlePaySkorProtect = async (pendingAction) => {
    try {
      setPaymentProcessingId(pendingAction.skor_protect_id);
      const paymentUrl = await createSkorProtectPaymentLink(
        pendingAction.skor_protect_id
      );
      if (!paymentUrl) throw new Error("Payment URL was not returned");
      window.location.assign(paymentUrl);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ??
          _t("failedToCreateSkorProtectPaymentLink")
      );
      loadPendingSkorProtectActions();
      setPaymentProcessingId(null);
    }
  };

  const handleViewPaymentReceipt = async (pendingAction) => {
    const receiptWindow = window.open("", "_blank");
    if (receiptWindow) receiptWindow.opener = null;

    try {
      const receiptUrl = await getSkorProtectPaymentReceipt(
        pendingAction.skor_protect_id
      );
      if (!receiptUrl) throw new Error("Receipt URL was not returned");

      if (receiptWindow) {
        receiptWindow.location.href = receiptUrl;
      } else {
        window.location.assign(receiptUrl);
      }
    } catch (err) {
      receiptWindow?.close();
      toast.error(
        err?.response?.data?.message ?? _t("failedToLoadPaymentReceipt")
      );
    }
  };

  if (!isOpen) return null;

  return (
    <Drawer
      open={isOpen}
      onClose={onClose}
      title={_t("candidacies", "Candidacies")}
      description={
        llShowOnly
          ? _t(
              "applicationModelDescriptionLandlord",
              "The people applying together in this application."
            )
          : _t(
              "applicationModelDescription",
              "Introduce yourself to the landlord together with the people you will be living with."
            )
      }
      closeLabel={_t("close")}
      size="md"
      footer={
        !llShowOnly && (
          <Button
            size="lg"
            iconStart={<Icon name="plus" />}
            onClick={() => {
              onClose();
              onNewApplication();
            }}
          >
            {_t("newApplication")}
          </Button>
        )
      }
    >
      <div className="applicationsDrawer__list">
        {loading ? (
          <SkeletonList rows={3} variant="card" label={_t("loading")} />
        ) : (applications ?? []).length === 0 ? (
          <p className="applications-empty">
            {_t(
              "noApplicationsYet",
              "You don't have any applications yet. Create one and invite your flatmates."
            )}
          </p>
        ) : (
          (applications ?? []).map((app) => (
            <ApplicationCard
              key={app._id}
              app={app}
              currentUserEmail={currentUserEmail}
              currentUserId={currentUserId}
              currentUserClientIdentificationStatus={
                user?.consents?.client_identification_consent?.status
              }
              onAccept={handleAccept}
              onReject={handleReject}
              onDelete={handleDelete}
              processingId={processingId}
              openMenuId={openMenuId}
              setOpenMenuId={setOpenMenuId}
              onCloseModal={onClose}
              llShowOnly={llShowOnly}
              onSignClientIdentification={handleSignClientIdentification}
              pendingSkorProtectAction={pendingSkorProtectActions.find(
                (action) => String(action.application_id) === String(app._id)
              )}
              now={now}
              onPaySkorProtect={handlePaySkorProtect}
              paymentProcessingId={paymentProcessingId}
              onViewPaymentReceipt={handleViewPaymentReceipt}
            />
          ))
        )}
      </div>
    </Drawer>
  );
};

export default ApplicationsModal;
